import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Alert, Button, TabBar } from '@thegpvc/ui';
import { Check, X, Lightbulb, RotateCcw } from 'lucide-react';
import { api } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { PageContainer } from '../components/PageContainer';
import { ConstraintCandidates } from '../components/ConstraintCandidates';
import { text, cx } from '../styles/theme';
import { formatTimestamp } from '../utils/format';
import { errorMessage } from '../utils/errorMessage';
import type { SemanticPattern } from '../types/api';

type TabId = 'new' | 'accepted' | 'rejected' | 'resolved';
type StatusFilter = Exclude<TabId, 'resolved'>;

function statusOf(p: SemanticPattern): StatusFilter {
  if (p.dismissed) return 'rejected';
  if (p.accepted) return 'accepted';
  return 'new';
}

const PATTERN_TYPE_COLORS: Record<string, string> = {
  recurring_blocker: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  workflow_habit: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  tool_preference: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
  codebase_friction: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  // Cross-session L3 coaching insights.
  skill_candidate: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
  agents_md_candidate: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
};

// Human-friendly labels for pattern types where the raw underscore form reads
// poorly. Falls back to the de-underscored type.
const PATTERN_TYPE_LABELS: Record<string, string> = {
  skill_candidate: 'skill opportunity',
  agents_md_candidate: 'codify rule',
};

function PatternTypeChip({ type }: { type: string }) {
  const colors = PATTERN_TYPE_COLORS[type] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  const label = PATTERN_TYPE_LABELS[type] || type.replace(/_/g, ' ');
  return (
    <span className={cx('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', colors)}>
      {label}
    </span>
  );
}

function ConfidenceMeter({ confidence }: { confidence: number }) {
  const pct = Math.round(confidence * 100);
  // The bar encodes confidence by width alone, so the value is invisible to
  // assistive tech — mirror the shared bar primitives (CompositionBar,
  // FrictionQualityCard's "Turn signals") by labelling the whole meter
  // role="img" with the same string as the tooltip, so the "confidence" context
  // (not just a bare "60%") reaches a screen reader and can't drift from the
  // visible title.
  return (
    <span
      role="img"
      aria-label={`${pct}% confidence`}
      title={`${pct}% confidence`}
      className="inline-flex items-center gap-1.5"
    >
      <span aria-hidden="true" className="h-1.5 w-14 rounded-full bg-gray-200 dark:bg-navy-700">
        <span className="block h-full rounded-full bg-orange-400 dark:bg-orange-500" style={{ width: `${pct}%` }} />
      </span>
      <span className="tabular-nums">{pct}%</span>
    </span>
  );
}

type Feedback = 'dismiss' | 'accept' | 'clear';

function PatternCard({
  pattern,
  onFeedback,
  pending,
}: {
  pattern: SemanticPattern;
  onFeedback: (id: string, feedback: Feedback) => void;
  pending: boolean;
}) {
  const projects = pattern.projects ?? [];
  const evidence = pattern.evidence ?? [];

  return (
    <div
      className={cx(
        'rounded-lg border p-4',
        pattern.accepted && 'border-emerald-200 bg-emerald-50/40 dark:border-emerald-900/40 dark:bg-emerald-900/10',
        pattern.dismissed && 'border-gray-200 bg-gray-50/60 opacity-70 dark:border-navy-800 dark:bg-navy-900/30',
        !pattern.accepted && !pattern.dismissed && 'border-gray-200 bg-white dark:border-navy-700 dark:bg-navy-800/40',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className={cx('text-base font-semibold', text.primary)}>{pattern.title}</h3>
            <PatternTypeChip type={pattern.patternType} />
            {pattern.accepted && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                <Check className="h-3 w-3" /> Accepted
              </span>
            )}
            {pattern.dismissed && (
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-navy-700 dark:text-navy-300">
                Dismissed
              </span>
            )}
          </div>
          <p className={cx('mt-1 text-sm', text.secondary)}>{pattern.description}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={pending}
            aria-label={pattern.accepted ? 'Un-accept insight' : 'Accept insight'}
            title={pattern.accepted ? 'Un-accept' : 'Accept — pin this insight'}
            onClick={() => onFeedback(pattern.id, pattern.accepted ? 'clear' : 'accept')}
            className={pattern.accepted ? 'text-emerald-600 dark:text-emerald-400' : text.secondary}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={pending}
            aria-label={pattern.dismissed ? 'Restore insight' : 'Dismiss insight'}
            title={pattern.dismissed ? 'Restore — un-hide this insight' : 'Dismiss — hide this insight'}
            onClick={() => onFeedback(pattern.id, pattern.dismissed ? 'clear' : 'dismiss')}
            className={text.secondary}
          >
            {pattern.dismissed ? <RotateCcw className="h-4 w-4" /> : <X className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {pattern.recommendation && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-orange-100 bg-orange-50 px-3 py-2 dark:border-orange-900/30 dark:bg-orange-900/15">
          <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-orange-500 dark:text-orange-400" />
          <p className="text-sm">
            <span className="font-medium text-orange-800 dark:text-orange-300">Try: </span>
            <span className={text.secondary}>{pattern.recommendation}</span>
          </p>
        </div>
      )}

      <div className={cx('mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs', text.muted)}>
        <ConfidenceMeter confidence={pattern.confidence} />
        <span>
          Recurred <span className="tabular-nums font-medium">{pattern.occurrenceCount}</span>{' '}
          {pattern.occurrenceCount === 1 ? 'time' : 'times'}
        </span>
        <span>Last seen {formatTimestamp(pattern.lastSeenAt)}</span>
        {projects.length > 0 && (
          <span className="flex flex-wrap items-center gap-1">
            {projects.map((p) => (
              <span
                key={p}
                className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-navy-600 dark:bg-navy-700 dark:text-navy-200"
              >
                {p}
              </span>
            ))}
          </span>
        )}
      </div>

      {evidence.length > 0 && (
        <details className="mt-3">
          <summary className={cx('cursor-pointer text-xs font-medium', text.link)}>
            Why — {evidence.length} {evidence.length === 1 ? 'example' : 'examples'}
          </summary>
          <ul className="mt-2 space-y-2">
            {evidence.map((e, i) => (
              <li key={i} className="border-l-2 border-gray-200 pl-3 dark:border-navy-700">
                {(e.project || e.sessionId) && (
                  <div className={cx('flex items-center gap-2 text-xs', text.muted)}>
                    {e.project && <span>{e.project}</span>}
                    {e.sessionId && (
                      <Link to={`/sessions/${e.sessionId}`} className={cx('font-mono', text.link)}>
                        {e.sessionId.slice(0, 8)}…
                      </Link>
                    )}
                  </div>
                )}
                {e.description && <p className={cx('text-sm', text.primary)}>{e.description}</p>}
                {e.quote && <p className={cx('text-sm italic', text.secondary)}>&ldquo;{e.quote}&rdquo;</p>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// fmtDay renders a unix-seconds timestamp as an explicit date (always with the
// year) for resolved-pattern lifespans.
function fmtDay(ts: string): string {
  const n = parseInt(ts, 10);
  if (!n || isNaN(n)) return 'unknown';
  return new Date(n * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function quietDays(lastSeenAt: string): number {
  const n = parseInt(lastSeenAt, 10);
  if (!n || isNaN(n)) return 0;
  return Math.max(0, Math.floor((Date.now() / 1000 - n) / 86400));
}

/**
 * ResolvedCard is the before/after proof: a pattern that genuinely recurred
 * (occurrence >= 2) and then stopped. Read-only — no feedback actions.
 */
function ResolvedCard({ pattern }: { pattern: SemanticPattern }) {
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-4 dark:border-emerald-900/40 dark:bg-emerald-900/10">
      <div className="flex flex-wrap items-center gap-2">
        <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        <h3 className={cx('text-base font-semibold', text.primary)}>{pattern.title}</h3>
        <PatternTypeChip type={pattern.patternType} />
      </div>
      {pattern.description && <p className={cx('mt-1 text-sm', text.secondary)}>{pattern.description}</p>}
      <div className={cx('mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs', text.muted)}>
        <span>
          Recurred <span className="tabular-nums font-medium">{pattern.occurrenceCount}</span>{' '}
          {pattern.occurrenceCount === 1 ? 'time' : 'times'}, {fmtDay(pattern.firstSeenAt)} &ndash;{' '}
          {fmtDay(pattern.lastSeenAt)}
        </span>
        <span className="font-medium text-emerald-700 dark:text-emerald-400">
          Quiet for {quietDays(pattern.lastSeenAt)} days
        </span>
      </div>
    </div>
  );
}

/**
 * Insights is the cross-session workbench: deduped, recommendation-gated
 * recurring patterns (Tier 3), each acceptable (pin) or dismissable (hide,
 * persistently), and each grounded in the projects/sessions it was observed in.
 * Per-session friction lives on Session Detail + the Sessions list.
 */
export function Insights() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<TabId>('new');

  const { data, isLoading, error } = useQuery({
    queryKey: ['semanticPatterns'],
    queryFn: () => api.listSemanticPatterns(),
  });

  // Resolved patterns load lazily when the tab is first opened.
  const resolved = useQuery({
    queryKey: ['semanticPatterns', 'resolved'],
    queryFn: () => api.listSemanticPatterns('resolved'),
    enabled: filter === 'resolved',
  });

  const feedback = useMutation({
    mutationFn: ({ id, feedback }: { id: string; feedback: Feedback }) => api.setPatternFeedback(id, feedback),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['semanticPatterns'] }),
  });

  const patterns = data?.patterns || [];
  const counts: Record<StatusFilter, number> = { new: 0, accepted: 0, rejected: 0 };
  for (const p of patterns) counts[statusOf(p)]++;
  const visible = filter === 'resolved' ? [] : patterns.filter((p) => statusOf(p) === filter);
  const resolvedPatterns = resolved.data?.patterns || [];

  const tabs = [
    { id: 'new', label: counts.new ? `New · ${counts.new}` : 'New' },
    { id: 'accepted', label: counts.accepted ? `Accepted · ${counts.accepted}` : 'Accepted' },
    { id: 'rejected', label: counts.rejected ? `Rejected · ${counts.rejected}` : 'Rejected' },
    { id: 'resolved', label: resolved.data ? `Resolved · ${resolvedPatterns.length}` : 'Resolved' },
  ];

  const emptyCopy: Record<TabId, string> = {
    new: 'No new patterns. They emerge after recurring friction across multiple sessions.',
    accepted: 'No accepted patterns yet. Accept a pattern to pin it here.',
    rejected: 'Nothing dismissed. Patterns you dismiss land here — restore them anytime.',
    resolved: 'Nothing resolved yet. Patterns that recurred and then stopped for 30+ days land here.',
  };

  return (
    <PageContainer width="narrow">
      <title>Dixie :: Insights</title>
      <PageHeader title="Insights" count={patterns.length || undefined}>
        <TabBar
          variant="pill"
          items={tabs}
          activeId={filter}
          onChange={(id: string) => setFilter(id as TabId)}
        />
      </PageHeader>

      <p className={cx('text-sm', text.secondary)}>
        Cross-session friction that recurred — accept to pin, dismiss to hide.
      </p>

      {/* Direction axis: recurring constraints to codify, alongside the
          execution-axis coaching patterns below. User-scoped. */}
      <ConstraintCandidates />

      {feedback.isError && (
        <Alert variant="error">Failed to save feedback: {errorMessage(feedback.error)}</Alert>
      )}
      {error && <Alert variant="error">Failed to load patterns: {errorMessage(error)}</Alert>}

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-lg bg-gray-100 dark:bg-navy-800" />
          ))}
        </div>
      )}

      {filter === 'resolved' && resolved.isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-gray-100 dark:bg-navy-800" />
          ))}
        </div>
      )}
      {filter === 'resolved' && resolved.isError && (
        <Alert variant="error">Failed to load resolved patterns: {errorMessage(resolved.error)}</Alert>
      )}
      {filter === 'resolved' && resolved.data && resolvedPatterns.length === 0 && (
        <p className={cx('py-10 text-center text-sm', text.secondary)}>{emptyCopy.resolved}</p>
      )}
      {filter === 'resolved' && resolvedPatterns.length > 0 && (
        <div className="space-y-3">
          {resolvedPatterns.map((p) => (
            <ResolvedCard key={p.id} pattern={p} />
          ))}
        </div>
      )}

      {filter !== 'resolved' && !isLoading && !error && visible.length === 0 && (
        <p className={cx('py-10 text-center text-sm', text.secondary)}>{emptyCopy[filter]}</p>
      )}

      {filter !== 'resolved' && !isLoading && visible.length > 0 && (
        <div className="space-y-3">
          {visible.map((p) => (
            <PatternCard
              key={p.id}
              pattern={p}
              // Scope the disabled state to the card being mutated so accepting
              // one pattern doesn't freeze every other card's buttons.
              pending={feedback.isPending && feedback.variables?.id === p.id}
              onFeedback={(id, fb) => feedback.mutate({ id, feedback: fb })}
            />
          ))}
        </div>
      )}
    </PageContainer>
  );
}
